import { useEffect, useState } from 'react';
import { Trophy, X } from 'lucide-react';

interface Achievement {
  id: number;
  code: string;
  name: string;
  description: string;
  icon: string;
  reward_points: number;
}

interface AchievementToastProps {
  achievement: Achievement;
  onClose: () => void;
  duration?: number;
}

export function AchievementToast({ achievement, onClose, duration = 5000 }: AchievementToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Animate in
    setTimeout(() => setIsVisible(true), 50);

    // Auto close
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(onClose, 300);
  };

  return (
    <div
      className={`
        fixed top-4 right-4 z-[9999] max-w-sm
        transform transition-all duration-300 ease-out
        ${isVisible && !isLeaving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <div className="bg-gradient-to-r from-yellow-900/90 to-amber-900/90 backdrop-blur-md rounded-xl border border-yellow-500/50 shadow-2xl shadow-yellow-500/20 overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-yellow-500/30">
          <div
            className="h-full bg-yellow-400 transition-all ease-linear"
            style={{
              width: '100%',
              animation: `shrink ${duration}ms linear forwards`
            }}
          />
        </div>

        <div className="p-4 flex items-start gap-4">
          {/* Icon */}
          <div className="flex-shrink-0 w-14 h-14 rounded-full bg-yellow-500/20 border-2 border-yellow-400 flex items-center justify-center text-3xl animate-bounce">
            {achievement.icon || <Trophy className="w-7 h-7 text-yellow-400" />}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-yellow-400 text-xs font-semibold uppercase tracking-wider">
                Достижение разблокировано!
              </span>
            </div>
            <h4 className="text-white font-bold text-lg truncate">
              {achievement.name}
            </h4>
            <p className="text-yellow-200/80 text-sm line-clamp-2">
              {achievement.description}
            </p>
            {achievement.reward_points > 0 && (
              <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/20 rounded-full">
                <span className="text-yellow-400 text-sm font-bold">+{achievement.reward_points}</span>
                <span className="text-yellow-400/80 text-xs">очков</span>
              </div>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={handleClose}
            className="flex-shrink-0 p-1 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-yellow-400/70 hover:text-yellow-400" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}

// Container for multiple toasts
interface AchievementToastContainerProps {
  achievements: Achievement[];
  onClear: () => void;
}

export function AchievementToastContainer({ achievements, onClear }: AchievementToastContainerProps) {
  const [queue, setQueue] = useState<Achievement[]>([]);
  const [current, setCurrent] = useState<Achievement | null>(null);

  useEffect(() => {
    if (achievements.length > 0) {
      setQueue(prev => [...prev, ...achievements]);
      onClear();
    }
  }, [achievements, onClear]);

  useEffect(() => {
    if (!current && queue.length > 0) {
      setCurrent(queue[0]);
      setQueue(prev => prev.slice(1));
    }
  }, [current, queue]);

  const handleClose = () => {
    setCurrent(null);
  };

  if (!current) return null;

  return <AchievementToast achievement={current} onClose={handleClose} />;
}
